/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { TransferOrderDetailsByTransferIdApiResponse } from "../../../@types/DRO/TransferOrderResponse";
import { ReturnTransferOrderBody } from "../../../@types/DTO/CreateTransferOrderBody";
import { DownloadPdfApi } from "../../../api/PdfDownload/DownloadPdfApi";
import { TransferOrderApi } from "../../../api/TransferOrder/TransferOrderApi";
import { TransferOrderHistory } from "../OrderHistory/TransferOrderHistory";

const ReturnToSourceTransferOrder: React.FC = () => {
  const params = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const [isSuccess, setIsSuccess] = useState(false);
  const [transferDetails, setTransferDetails] =
    useState<TransferOrderDetailsByTransferIdApiResponse>();
  const [orderId, setOrderId] = useState<number>(-1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [quantityReturned, setQuantityReturned] = useState<{
    [key: number]: number;
  }>({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [permittedActions, setPermittedActions] = useState<string[]>([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const getTransferDetails = useCallback(
    async (orderId: number) => {
      if (orderId <= 0) return;
      try {
        const response =
          await TransferOrderApi.api.getTransferOrderDetailsByTransferId(
            orderId
          );
        setTransferDetails(response);
        setPermittedActions(response.data.permittedAction || []);
      } catch (error) {
        console.error("Error fetching parts", error);
      }
    },
    [orderId]
  );

  useEffect(() => {
    if (params.orderId && !isNaN(+params.orderId)) {
      setOrderId(+params.orderId);
    }
  }, [params.orderId]);

  useEffect(() => {
    getTransferDetails(orderId);
  }, [orderId, getTransferDetails]);

  const handleDownloadPdf = async () => {
    if (orderId <= 0) {
      setSnackbarMessage("Invalid Transfer Order ID");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsDownloading(true);
      await DownloadPdfApi.api.getPdfLinkByTOId(orderId);
      setSnackbarMessage("PDF downloaded successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      setSnackbarMessage("Failed to download PDF. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleReturnToSource = async () => {
    if (!transferDetails) return;

    const orderBody: ReturnTransferOrderBody = {
      orderId: orderId,
      orderStatus: "COMPLETED",
      orderReturnList: transferDetails.data.orderLineDetailsList.map(
        (item) => ({
          partId: item.partId,
          quantity: quantityReturned[item.partId] || 0,
          returnReason: "Return to source",
        })
      ),
    };

    try {
      await TransferOrderApi.api.createReturnTransferOrder(orderBody);
      setSnackbarMessage("Return transfer order created successfully");
      setSnackbarSeverity("success");
      setIsSuccess(true);
    } catch (error) {
      setSnackbarMessage("Error creating return transfer order");
      setSnackbarSeverity("error");
      setIsSuccess(false);
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      const response = await TransferOrderApi.api.changeTransferOrderStatus(
        orderId,
        newStatus
      );
      setIsSuccess(true);
      setSnackbarMessage(
        response?.message ||
          `Transfer order status changed to ${newStatus} successfully.`
      );
      setSnackbarSeverity("success");
    } catch (error: any) {
      console.error(
        `Error changing transfer order status to ${newStatus}`,
        error
      );
      setIsSuccess(false);
      setSnackbarMessage(
        error?.response?.data?.message ||
          error?.data?.message ||
          error?.message ||
          `Failed to change transfer order status to ${newStatus}.`
      );
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleQuantityChange = (partId: number, value: string) => {
    if (!transferDetails) return;

    // Allow empty string for clearing the field
    if (value === "") {
      setQuantityReturned((prev) => ({
        ...prev,
        [partId]: 0,
      }));
      return;
    }

    // Only allow numeric input
    if (!/^\d+$/.test(value)) {
      return;
    }

    const receivedQuantity =
      transferDetails.data.orderLineDetailsList.find(
        (item) => item.partId === partId
      )?.quantityReceived || 0;

    const newQuantity = Number(value);

    if (newQuantity > receivedQuantity) {
      setSnackbarMessage(
        `Quantity returned cannot exceed quantity received (${receivedQuantity}) for this item.`
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      // Don't update the value if it exceeds the limit
      return;
    }

    setQuantityReturned((prev) => ({
      ...prev,
      [partId]: newQuantity,
    }));
  };

  const renderActionButtons = () => {
    const buttons = [];

    buttons.push(
      <Button
        key="return-to-source"
        onClick={handleReturnToSource}
        variant="contained"
        color="primary"
        sx={{
          padding: "8px 20px",
          borderRadius: "32px",
          backgroundColor: "#1E1E1E",
          textTransform: "none",
          fontWeight: "500",
          fontSize: "0.875rem",
          "&:hover": { backgroundColor: "#000000" },
        }}>
        Return to Source
      </Button>
    );

    // Check for CANCELED permission
    if (permittedActions.includes("CANCELED")) {
      buttons.push(
        <Button
          key="cancel"
          onClick={() => handleStatusChange("CANCELED")}
          variant="outlined"
          sx={{
            padding: "8px 20px",
            borderRadius: "32px",
            borderColor: "#FF0000",
            color: "#FF0000",
            textTransform: "none",
            fontWeight: "500",
            fontSize: "0.875rem",
            "&:hover": {
              borderColor: "#CC0000",
              backgroundColor: "rgba(255, 0, 0, 0.04)",
            },
          }}>
          Cancel Order
        </Button>
      );
    }

    return buttons;
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="w-full p-4">
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "600",
                color: "#1E1E1E",
                textAlign: "left",
                fontSize: "1.5rem",
                mb: 0,
              }}>
              Delivery Details - Return to Source
            </Typography>

            {orderId > 0 && (
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <TransferOrderHistory
                  transferOrderId={orderId}
                  variant="dialog"
                  open={historyDialogOpen}
                  onClose={() => setHistoryDialogOpen(false)}
                />
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => setHistoryDialogOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 500, borderRadius: "8px" }}>
                  History
                </Button>
                <Button
                  variant="contained"
                  startIcon={
                    isDownloading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={handleDownloadPdf}
                  disabled={isDownloading}
                  sx={{
                    backgroundColor: "#1366D9",
                    color: "white",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      backgroundColor: "#0f5cc0",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                    },
                    "&:disabled": {
                      backgroundColor: "#B0C4DE",
                    },
                  }}>
                  {isDownloading ? "Downloading..." : "Download PDF"}
                </Button>
              </Box>
            )}
          </Box>

          <div className="flex items-center bg-[#F8F9FA] px-4 py-3 rounded-lg space-x-2">
            <Link
              to="/home"
              className="text-sm font-medium text-blue-600 hover:text-blue-600 hover:underline transition-colors">
              Dashboard
            </Link>
            <span className="text-sm text-[#646464] mx-1">&gt;</span>
            <Link
              to={`/transfer-order${statusFilter ? `?status=${statusFilter}` : ""}`}
              className="text-sm font-medium text-blue-600 hover:text-blue-600 hover:underline transition-colors">
              Transfer Order List
            </Link>
            <span className="text-sm text-[#646464] mx-1">&gt;</span>
            <span className="text-sm font-medium text-gray-800">
              Transfer Order Details
            </span>
          </div>
        </Box>
        <Paper
          sx={{
            p: 4,
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            backgroundColor: "white",
            maxWidth: "1600px",
            margin: "0 auto",
          }}>
          {/* Form Section */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Source Location
                  </label>
                  <input
                    readOnly
                    className="w-full border-b focus:outline-none p-1 text-sm"
                    value={
                      transferDetails?.data.sourceWarehouse.shortName ?? ""
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Destination Location
                  </label>
                  <input
                    readOnly
                    className="w-full border-b focus:outline-none p-1 text-sm"
                    value={
                      transferDetails?.data.destinationWarehouse.shortName ?? ""
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Source TO
                  </label>
                  <input
                    readOnly
                    className="w-full border-b focus:outline-none p-1 text-sm"
                    value={transferDetails?.data.parentId ?? "-"}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Created By
                  </label>
                  <input
                    readOnly
                    className="w-full border-b focus:outline-none p-1 text-sm"
                    value={transferDetails?.data.createdBy ?? "N/A"}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Items Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: "left",
                fontWeight: "600",
                color: "#1E1E1E",
                mb: 2,
                fontSize: "1.125rem",
              }}>
              Items
            </Typography>
            <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 80,
                      }}>
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 200,
                      }}>
                      Product Description
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 150,
                      }}>
                      Reference Code
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 120,
                      }}>
                      Quantity Ordered
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 120,
                      }}>
                      Received
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 120,
                      }}>
                      Quantity Returned
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 150,
                      }}>
                      Unit Of Measure
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 120,
                      }}>
                      {/* Actions or empty */}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {transferDetails?.data.orderLineDetailsList.map(
                    (item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#ffffff" },
                          "&:hover": { backgroundColor: "#e3f2fd" },
                        }}>
                        <TableCell sx={{ fontSize: "0.875rem" }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.875rem" }}>
                          {item.description}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.875rem" }}>
                          {item.referenceCode}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.875rem" }}>
                          {item.quantityOrdered}
                        </TableCell>
                        <TableCell>{item.quantityReceived}</TableCell>
                        <TableCell>
                          <TextField
                            type="text"
                            value={quantityReturned[item.partId] || ""}
                            onChange={(event) =>
                              handleQuantityChange(
                                item.partId,
                                event.target.value
                              )
                            }
                            placeholder="0"
                            InputProps={{
                              style: { fontSize: "0.875rem" },
                              inputMode: "numeric",
                            }}
                            size="small"
                            sx={{
                              width: "100px",
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1366D9",
                                },
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>UNIT</TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}>
              {renderActionButtons()}
            </Box>
          </Box>
        </Paper>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default ReturnToSourceTransferOrder;
